import { createFeatureSelector, createSelector, select } from '@ngrx/store';
import type { ArticlesState, ArticlesPartialState } from './articles.reducer';
import { ARTICLES_FEATURE_KEY, articlesAdapter } from './articles.reducer';
import type { ArticleEntity, BaseArticleEntityWithEnrichedArticle } from './articles.models';
import { createArticleModelEnrichment } from '../transformer/elements-transformer';
import { pipe } from 'rxjs';
import { distinctUntilChanged, filter, map, shareReplay } from 'rxjs/operators';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { ARTICLE_COMPONENT_MAPPING, ComponentMapping } from '@candyland/angular/shared/article-tt-com/model';

// Lookup the 'Articles' feature state managed by NgRx
export const selectArticlesState = createFeatureSelector<ArticlesState>(ARTICLES_FEATURE_KEY);

const { selectAll, selectEntities } = articlesAdapter.getSelectors();

export const selectAllArticles = createSelector(selectArticlesState, (state: ArticlesState) => selectAll(state));

export const selectArticlesEntities = createSelector(selectArticlesState, (state: ArticlesState) =>
  selectEntities(state)
);

export const selectCurrentArticleCmsId = createSelector(selectArticlesState, (state) => state.articleIdOfCurrentPage);

export const selectCurrentArticle = createSelector(
  selectArticlesEntities,
  selectCurrentArticleCmsId,
  (entities, id) => entities[id]
);

export const selectCurrentArticleUUID = createSelector(
  selectArticlesState,
  (state) => state.articleIdOfCurrentPreviewPage
);

export const selectCurrentArticleEntity = createSelector(
  selectArticlesEntities,
  selectCurrentArticleCmsId,
  (articlesEntities, cmsId) => articlesEntities[cmsId]
);

export const getArticleByCmsId = createSelector<
  ArticlesPartialState,
  { cmsId: string },
  Record<string, ArticleEntity>,
  ArticleEntity
>(selectArticlesEntities, (articleEntities, props) => articleEntities[props.cmsId]);

export function getFetchDate(articleEntity: ArticleEntity): number | null {
  return articleEntity.error === null ? (articleEntity.fetchDate ? Date.parse(articleEntity.fetchDate) : null) : null;
}

@Injectable({ providedIn: 'root' })
export class CurrentArticle {
  private selectCurrentEnrichedArticleEntity = createSelector(
    selectCurrentArticleEntity,
    (articleEntity: ArticleEntity): BaseArticleEntityWithEnrichedArticle =>
      articleEntity && {
        ...articleEntity,
        articleData: articleEntity.articleData && {
          ...articleEntity.articleData,
          ...createArticleModelEnrichment(this.articleComponentMapping, articleEntity.articleData, this.platformId),
        },
      }
  );

  public getArticleEntity = pipe(
    select(this.selectCurrentEnrichedArticleEntity),
    filter((articleEntity: BaseArticleEntityWithEnrichedArticle) => !!articleEntity),
    shareReplay({ refCount: true, bufferSize: 1 })
  );

  public getArticle = pipe(
    this.getArticleEntity,
    distinctUntilChanged(
      (prev: BaseArticleEntityWithEnrichedArticle, curr: BaseArticleEntityWithEnrichedArticle) =>
        prev.id === curr.id && getFetchDate(prev) === getFetchDate(curr)
    ),
    map((articleEntity: BaseArticleEntityWithEnrichedArticle) => articleEntity.articleData),
    shareReplay({ refCount: true, bufferSize: 1 })
  );

  public getArticleEntityWithError = pipe(
    select(this.selectCurrentEnrichedArticleEntity),
    filter((articleEntity: BaseArticleEntityWithEnrichedArticle) => articleEntity && !!articleEntity.error)
  );
  public articleCmsId = selectCurrentArticleCmsId;
  public articleUUID = selectCurrentArticleUUID;

  constructor(
    @Inject(ARTICLE_COMPONENT_MAPPING) private articleComponentMapping: ComponentMapping,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}
}
